import { FaTicketAlt, FaGift, FaTag, FaBoxOpen } from "react-icons/fa";
import { formatPrice, getMemberEffectivePrice, calculateOrderTax } from "../../services/ticketingService";
import "./OrderSummary.css";

/**
 * Order summary — shows selected line items, promos, tax, and total.
 *
 * Props:
 *  - tiers: array of tier objects
 *  - quantities: { [tier_id]: number }
 *  - promotions: array of active promotions
 *  - appliedPromo: { code, discount_cents, description } | null
 */
export default function OrderSummary({ tiers, quantities, promotions, appliedPromo, packs, packQuantities }) {
  const lineItems = tiers
    .filter((t) => (quantities[t.tier_id] || 0) > 0)
    .map((t) => {
      const effectivePrice = getMemberEffectivePrice(t, tiers);
      return {
        key: `tier-${t.tier_id}`,
        name: t.name,
        price_cents: effectivePrice,
        quantity: quantities[t.tier_id],
        subtotal: effectivePrice * quantities[t.tier_id],
        isPack: false,
      };
    });

  const packLineItems = (packs || [])
    .filter((p) => ((packQuantities || {})[p.pack_id] || 0) > 0)
    .map((p) => ({
      key: `pack-${p.pack_id}`,
      name: `${p.name} (${p.quantity} tickets)`,
      price_cents: p.pack_price_cents,
      quantity: (packQuantities || {})[p.pack_id],
      subtotal: p.pack_price_cents * (packQuantities || {})[p.pack_id],
      isPack: true,
    }));

  const allLineItems = [...lineItems, ...packLineItems];
  const subtotal = allLineItems.reduce((sum, item) => sum + item.subtotal, 0);

  const discountCents = appliedPromo?.discount_cents || 0;
  const discountRatio = (discountCents > 0 && subtotal > 0)
    ? Math.min(1, discountCents / subtotal)
    : 0;
  const tierTaxCents = calculateOrderTax(tiers, quantities, discountCents, subtotal);
  const packTaxCents = (packs || []).reduce((sum, p) => {
    const qty = (packQuantities || {})[p.pack_id] || 0;
    if (qty === 0) return sum;
    const taxableAmount = Math.round(p.pack_price_cents * qty * (1 - discountRatio));
    return sum + Math.round(taxableAmount * ((p.tax_percent || 0) / 100));
  }, 0);
  const taxCents = tierTaxCents + packTaxCents;
  const total = Math.max(0, subtotal - discountCents + taxCents);

  // Evaluate promo nudges
  const promoNudges = (promotions || []).map((promo) => {
    const totalQty = promo.applies_to_tier_id
      ? (quantities[promo.applies_to_tier_id] || 0)
      : Object.values(quantities).reduce((s, q) => s + q, 0);
    const timesTriggered = Math.floor(totalQty / promo.buy_qty);
    const freeCount = timesTriggered * promo.free_qty;
    const remaining = promo.buy_qty - (totalQty % promo.buy_qty);
    if (freeCount > 0) return { promo, type: "unlocked", freeCount };
    if (totalQty > 0 && remaining <= 2) return { promo, type: "pending", remaining };
    return null;
  }).filter(Boolean);

  if (allLineItems.length === 0) {
    return (
      <div className="order-summary order-summary--empty">
        <FaTicketAlt className="order-empty-icon" />
        <p>Select tickets above to see your order</p>
      </div>
    );
  }

  return (
    <div className="order-summary">
      <h3 className="order-summary-title">Order Summary</h3>

      <div className="order-summary-lines">
        {allLineItems.map((item) => (
          <div key={item.key} className="order-line">
            <div className="order-line-left">
              {item.isPack
                ? <FaBoxOpen className="order-line-icon" />
                : <FaTicketAlt className="order-line-icon" />
              }
              <span className="order-line-name">
                {item.name}
                <span className="order-line-qty"> × {item.quantity}</span>
              </span>
            </div>
            <span className="order-line-price">
              {item.price_cents === 0 ? "FREE" : formatPrice(item.subtotal)}
            </span>
          </div>
        ))}
      </div>

      {/* Promo nudges */}
      {promoNudges.map(({ promo, type, freeCount, remaining }) =>
        type === "unlocked" ? (
          <div key={promo.promo_id} className="promo-nudge promo-nudge--unlocked">
            <FaGift />
            <span>
              <strong>{freeCount} free ticket{freeCount > 1 ? "s" : ""} added!</strong>
              {" "}({promo.name})
            </span>
          </div>
        ) : (
          <div key={promo.promo_id} className="promo-nudge promo-nudge--pending">
            <FaGift />
            <span>
              Add {remaining} more to get {promo.free_qty} free ({promo.name})
            </span>
          </div>
        )
      )}

      <div className="order-summary-divider" />

      <div className="order-line order-line--subtotal">
        <span className="order-subtotal-label">Subtotal</span>
        <span className="order-line-price">{formatPrice(subtotal)}</span>
      </div>

      {discountCents > 0 && (
        <div className="order-line order-line--discount">
          <div className="order-line-left">
            <FaTag className="order-line-icon order-line-icon--promo" />
            <span className="order-line-name order-discount-label">
              {appliedPromo.code}
            </span>
          </div>
          <span className="order-line-price order-line-price--discount">
            &minus;{formatPrice(discountCents)}
          </span>
        </div>
      )}

      <div className="order-line order-line--tax">
        <span className="order-tax-label">Tax</span>
        <span className="order-line-price order-line-price--tax">
          {taxCents > 0 ? formatPrice(taxCents) : "$0.00"}
        </span>
      </div>
      <div className="order-summary-divider" />

      <div className="order-total">
        <span>Total</span>
        <strong>{total === 0 ? "FREE" : formatPrice(total)}</strong>
      </div>

      {total === 0 && (
        <p className="order-free-note">No payment required — tickets will be sent to your email.</p>
      )}
    </div>
  );
}
